import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { ProjectId, User } from 'api/models';
import { TeamReplacementProps } from 'api/teams';
import { toastService } from 'components/molecules/toast';
import { useTranslate } from 'hooks/use-translation';
import { getProjectMembersQueryKey } from 'queries';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { useAssignUserToTeamMutation } from 'queries/placeholder';
import { useAddProjectUsersLiteMutation } from 'queries/project';
import { getUserLabel } from 'utils/get-user-label';

interface ReturnProps {
  replaceTeamWithUser: (props: TeamReplacementProps) => Promise<any>;
  replacementInProgress: boolean;
}

interface Props {
  selectedReplacementUser?: User;
  successHandler?: () => void;
}

export const useReplaceTeamWithUser = ({
  selectedReplacementUser,
  successHandler,
}: Props): ReturnProps => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const userText = translate('user');

  const userName = useMemo(
    () =>
      selectedReplacementUser
        ? getUserLabel(selectedReplacementUser)
        : userText,
    [selectedReplacementUser, userText],
  );

  const {
    mutateAsync: replaceTeamWithUserMutation,
    isLoading: replacementInProgress,
  } = useAssignUserToTeamMutation();

  const {
    mutateAsync: addProjectUsersLite,
    isLoading: addProjectUsersLiteInProgress,
  } = useAddProjectUsersLiteMutation();

  const replaceTeamWithUser = ({
    projectId,
    teamId,
    userId,
    roleId,
  }: TeamReplacementProps) => {
    const errorHandler = () => {
      toastService({
        title: translate('resource_management.actions.find_member.error.title'),
        message: translate(
          'resource_management.actions.find_member.error.message',
          { userName },
        ),
      }).error();
    };

    return addProjectUsersLite(
      {
        projectId,
        projectUsers: [
          {
            userId,
            roleId,
            referenceTeamId: teamId,
          },
        ],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
          queryClient.invalidateQueries(
            getProjectMembersQueryKey(ProjectId(projectId)),
          );

          replaceTeamWithUserMutation(
            { projectId, teamId, userId, roleId },
            {
              onSuccess: () => {
                successHandler?.();
                toastService({
                  title: translate(
                    'resource_management.actions.find_member.success.title',
                  ),
                  message: translate(
                    'resource_management.actions.find_member.success.message',
                    { userName },
                  ),
                }).success();
              },
              onError: errorHandler,
            },
          );
        },
        onError: errorHandler,
      },
    );
  };

  return {
    replaceTeamWithUser,
    replacementInProgress:
      replacementInProgress || addProjectUsersLiteInProgress,
  };
};
